import { ChangeDetectionStrategy, Component, Injector, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { of, take } from 'rxjs';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { NativeBridgeService } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { BOOKING_STEPS, BOOKING_STEPS_ARRAY } from '@fcom/common/config/booking-config';
import { IconButtonSize, IconButtonTheme } from '@fcom/ui-components/components/buttons/icon-button/enums';
import { LanguageSelectorContext } from '@fcom/common/components/language-selector/language-selector.component';
import { GaContext } from '@fcom/common/interfaces/gtm.interface';
import { Step } from '@fcom/common/interfaces/step.interface';
import { BookingService } from '@fcom/booking/services/booking.service';
import { CountryAndLanguageSelection } from '@fcom/common/components/country-and-language-selector/country-and-language-selector.component';

import { Breadcrumb, PhasesAria } from '../../interfaces/booking-header-interfaces';

/**
 * This component contains the booking-style header used during booking-related flows.
 *
 * The header renders the shared booking header shell and stays sticky at the top of the page.
 * Layouts that render a booking sub-header beneath it should derive their offsets from the shared
 * header breakpoint tokens in `src/modules/assets/styles/variables.scss`.
 *
 * @example
 * <b>Example with basic options</b>
 *
 * <code>
 *   <pre>
 *     <fcom-booking-header
 *       [homeUrl]="'https://finnair.com/fi-fi'"
 *       [breadcrumbs]="breadcrumbs"
 *       [phasesAria]="phasesAria"
 *       [showLogin]="true"
 *       [showBack]="true"
 *       (loginClick)="onLogin($event)"
 *       (menuClick)="onMenu($event)"
 *       (homeClick)="onHome($event)"
 *       (breadcrumbClick)="onStepClick($event)"
 *     />
 *   </pre>
 * </code>
 */
@Component({
  selector: 'fcom-booking-header',
  templateUrl: './booking-header.component.html',
  styleUrls: ['./booking-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BookingHeaderComponent {
  private readonly nativeBridgeService = inject(NativeBridgeService);
  private readonly languageService = inject(LanguageService);
  private readonly bookingService = inject(BookingService);
  private readonly injector = inject(Injector);

  /**
   * What url the finnair logo should link to (including language)
   */
  readonly homeUrl = input<string | undefined>();

  /**
   * An array of breadcrumbs and their state
   */
  readonly breadcrumbs = input<Breadcrumb[] | undefined>([]);

  /**
   * @deprecated
   * Only used by the upgrade flow to force the back button to stay visible while it owns back
   * navigation outside the breadcrumb model.
   */
  readonly forceShowBack = input(false);

  /**
   * Controls the display of the login/logout button. The button should be hidden where the user
   * shouldn't be able to login or out.
   */
  readonly showLogin = input(true);

  readonly showBreadcrumbs = input(true);

  /**
   * Controls the display of the back button. The button should be hidden whether it isn't possible
   * to go back (for example on purchase confirmation pages)
   */
  readonly showBack = input(true);

  /**
   * Controls the propagation of the back button click event. If it is true, the event will be propagated
   * to be handled by the caller.
   */
  readonly propagateBackClick = input(false);

  /**
   * The translated text to be read in voice over.
   */
  readonly phasesAria = input.required<PhasesAria>();

  /**
   * Represents the current step in the booking process.
   */
  readonly currentStep = input<Step | undefined>();

  /**
   * Controls the visibility of the language selector in the booking header.
   */
  readonly showLanguageSelector = input(false);

  /**
   * The event emitted when the login button is clicked.
   */
  readonly loginClick = output<Event>();

  /**
   * The event emitted when the menu button is clicked.
   */
  readonly menuClick = output<Event>();

  /**
   * The event emitted when a breadcrumb item is clicked. This does not emit
   * when the breadcrumb.disabled property is true.
   */
  readonly breadcrumbClick = output<Breadcrumb>();

  /**
   * The event emitted when home button is clicked.
   *
   * If there is no listener for the event, the link will work as a regular a tag and redirect the
   * page. If there is a listener you must redirect the user to the page required (via angular
   * routing for example)
   */
  readonly homeClick = output<string>();

  /**
   * The event emitted when back button is clicked. This does not emit
   * when the propagateBack property is false.
   */
  readonly backClick = output<Event>();

  readonly isInsideNativeWebView = signal(this.nativeBridgeService.isInsideNativeWebView);
  private readonly isHamburgerMenuVisible = toSignal(this.nativeBridgeService.isHamburgerMenuVisible ?? of(false), {
    initialValue: false,
  });
  readonly showHamburgerMenu = computed(() => this.isInsideNativeWebView() && this.isHamburgerMenuVisible());
  readonly homeLink = computed(() => this.homeUrl() ?? this.languageService.langValue);
  readonly homeLink$ = toObservable(this.homeLink, { injector: this.injector });

  readonly CustomIcon = CustomIcon;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly LANGUAGE_SELECTOR_CONTEXT = LanguageSelectorContext.TOP_NAVI;
  readonly breadCrumbsWithoutSeat = computed<Breadcrumb[]>(() => {
    const breadcrumbs = this.breadcrumbs() ?? [];
    const isSeatBreadcrumbActive = breadcrumbs.find((b) => b.id === BOOKING_STEPS.SEATS.id)?.active;

    return !this.showBreadcrumbs()
      ? breadcrumbs
          .filter((b) => b.id !== BOOKING_STEPS.SEATS.id)
          .map((b) =>
            b.id === BOOKING_STEPS.ANCILLARIES.id ? { ...b, active: b.active || isSeatBreadcrumbActive } : b
          )
      : breadcrumbs;
  });
  readonly activeIndex = computed(() => {
    let currentIndex = -1;

    this.breadCrumbsWithoutSeat().forEach((breadcrumb, index) => {
      if (breadcrumb.active) {
        currentIndex = index;
      }
    });

    return currentIndex;
  });
  readonly hasBreadcrumbsToRender = computed(() => this.showBreadcrumbs() && this.breadCrumbsWithoutSeat().length > 0);

  onBreadcrumbClick(breadcrumb: Breadcrumb, index: number): void {
    if (breadcrumb.disabled) {
      return;
    }

    if (index < this.activeIndex()) {
      this.breadcrumbClick.emit(breadcrumb);
    }
  }

  onBackClick(event: Event): void {
    if (this.propagateBackClick()) {
      this.backClick.emit(event);
    } else if (this.activeIndex() > 0) {
      this.breadcrumbClick.emit(this.breadCrumbsWithoutSeat()[this.activeIndex() - 1]);
    }
  }

  onHomeClick(event: Event): void {
    event.preventDefault();
    this.homeClick.emit(this.homeLink());
  }

  onLoginButtonClick(event: Event): void {
    this.loginClick.emit(event);
  }

  activateSelectionPhase(locale?: string): void {
    const currentStep = this.currentStep();
    if (!currentStep) {
      return;
    }

    const bookingStep = BOOKING_STEPS_ARRAY.find((item) => item.id === currentStep.id);
    if (bookingStep) {
      this.bookingService.setStep({ step: bookingStep, locale });
    }
  }

  /**
   * If the user changes the country or language during the booking, we will redirect them back to the
   * flight selection step because of the country shops.
   */
  countryChanged(selection: CountryAndLanguageSelection): void {
    this.languageService.countryCode.pipe(take(1)).subscribe((currentCountryCode) => {
      if (currentCountryCode !== selection.country || selection.languageCode !== this.languageService.langValue) {
        this.bookingService.setStep({ step: BOOKING_STEPS.FLIGHT_SELECTION, locale: selection.languageCode });
      }
    });
  }

  protected readonly GaContext = GaContext;
}
